import { useState } from 'react';
import { IconLink } from '@tabler/icons-react';
import {
  ClientFeedbackAnswer,
  ClientFeedbackLink,
  ClientFeedbackState,
} from '../hooks/useClientFeedback';
import { Field } from './VisitDetailParts';

interface Props {
  state: ClientFeedbackState;
  answer: ClientFeedbackAnswer | null;
  link: ClientFeedbackLink | null;
}

const EVALUATION_LABELS: Record<string, string> = {
  price: 'Precio',
  size_layout: 'Tamaño y distribución',
  delivery_time: 'Tiempo de entrega',
  location: 'Ubicación',
  amenities: 'Amenidades',
  financing: 'Financiamiento',
  nothing_for_now: 'Nada por ahora',
  other: 'Otro',
};

const formatDate = (value: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('es');
};

const ClientFeedbackLinkCard = ({ state, answer, link }: Props) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const options = (answer?.evaluation_options || [])
    .map((option) => {
      if (option === 'other' && answer?.other_text) {
        return `Otro: ${answer.other_text}`;
      }
      return EVALUATION_LABELS[option] || option;
    })
    .join(', ');

  return (
    <section className="vd-card">
      <header className="vd-card__header">
        <span className="vd-icon vd-icon--green">
          <IconLink size={20} />
        </span>
        <h2 className="vd-card__title">Feedback del cliente</h2>
        {state === 'answered' && (
          <span className="vd-pill vd-pill--success ml-auto">Respondido</span>
        )}
        {state === 'pending' && (
          <span className="vd-pill vd-pill--warning ml-auto">Pendiente</span>
        )}
      </header>

      {state === 'loading' && <p className="mb-0 text-muted">Cargando…</p>}
      {state === 'error' && (
        <p className="mb-0 text-muted">No se pudo cargar el feedback.</p>
      )}
      {state === 'pending' && link && (
        <>
          <p className="vd-locked__text">
            El cliente aún no responde. Comparte este enlace con él:
          </p>
          <p className="vd-locked__text" style={{ wordBreak: 'break-all' }}>
            {link.url}
          </p>
          {formatDate(link.expires_at) && (
            <p className="mb-0 text-muted">
              Vence el {formatDate(link.expires_at)}
            </p>
          )}
          <div className="vd-actions">
            <button
              type="button"
              className="vd-btn vd-btn--primary"
              onClick={copy}
            >
              {copied ? 'Copiado' : 'Copiar enlace'}
            </button>
          </div>
        </>
      )}
      {state === 'answered' && answer && (
        <div className="vd-grid">
          <Field label="Satisfacción">
            {answer.satisfaction ? `${answer.satisfaction} / 5` : ''}
          </Field>
          <Field label="Interés">
            {answer.interest !== null ? `${answer.interest}%` : ''}
          </Field>
          <Field label="Lo que evaluó">{options}</Field>
          <Field label="Comentario">{answer.comment}</Field>
          <Field label="Respondido el">{formatDate(answer.answered_at)}</Field>
        </div>
      )}
    </section>
  );
};

export default ClientFeedbackLinkCard;
